<template>
  <v-sheet class="h80" elevation="4">
    <v-simple-table
      fixed-header
      v-if="items.length"
      :height="items.length ? '75vh' : null"
      width="100%"
    >
      <template v-slot:default>
        <thead>
          <tr>
            <th
              class="text-center text-capitalize"
              v-for="action in leftActions"
              :key="action.name"
            >
              {{ action.name }}
            </th>
            <th
              class="text-center text-capitalize"
              v-for="head in headers"
              :key="head.name"
            >
              {{ head.text }}
            </th>
            <th
              class="text-center text-capitalize"
              v-for="action in rightActions"
              :key="action.name"
            >
              {{ action.name }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item._id">
            <td
              class="text-center text-capitalize"
              v-for="action in leftActions"
              :key="action.name"
            >
              <ActionsColumn
                :action="action"
                :item="item"
                :scopeName="scopeName"
              />
            </td>
            <td class="text-center" v-for="head in headers" :key="head.name">
              <Tooltip
                :content="tooltipContent(head, item[head.value])"
                :active="tooltipContent(head, item[head.value]).length > 0"
                top
              >
                <section v-if="!head.type || head.type == 'text'">
                  <v-btn
                    plain
                    small
                    text
                    class="text-capitalize text-body-2 font-weight-medium"
                    v-if="head.to"
                    :to="head.to(item)"
                  >
                    {{ item[head.value] }}
                  </v-btn>
                  <span v-else class="text-body-2 my-1">
                    {{ item[head.value] }}
                  </span>
                  <copy-to-clip
                    v-if="head.copy"
                    :text="item[head.value]"
                    pos="right"
                  />
                </section>
                <section v-if="head.type == 'active'">
                  <v-icon small :color="item[head.value] ? 'success' : 'grey'">
                    mdi-toggle-switch{{
                      item[head.value] ? null : "-off-outline"
                    }}
                  </v-icon>
                </section>
                <section v-if="head.type == 'progress'">
                  <v-progress-circular
                    v-if="item[head.value] > 0"
                    :rotate="-90"
                    :size="40"
                    :width="5"
                    :value="item[head.value]"
                    color="success"
                  >
                    <strong>
                      <small>{{ item[head.value] }}% </small>
                    </strong>
                  </v-progress-circular>
                  <span v-else>- -</span>
                </section>
                <section v-if="head.type == 'type'">
                  <v-icon v-if="item[head.value]" color="indigo">
                    mdi-cog-clockwise
                  </v-icon>
                  <v-icon v-else color="info">mdi-flask-outline</v-icon>
                </section>
                <section v-if="head.type == 'test'">
                  <v-chip
                    small
                    :color="(item[head.value] ? 'green' : 'red') + ' lighten-5'"
                    :text-color="item[head.value] ? 'green' : 'red'"
                  >
                    <strong>{{
                      item[head.value] ? "Passed" : "Failed"
                    }}</strong>
                  </v-chip>
                </section>
                <section v-if="head.type == 'initiator'">
                  <v-icon color="light-blue" small>
                    mdi-{{ getInitiatorIcon(item[head.value] || 0) }}
                  </v-icon>
                  {{ initiatorName(item[head.value]) }}
                </section>
                <section v-if="head.type == 'date'">
                  {{
                    item[head.value] ? format(new Date(item[head.value])) : "--"
                  }}
                </section>
                <section v-if="head.type == 'role'">
                  <v-icon :color="getRoleColor(item[head.value])">
                    mdi-{{ getRoleIcon(item[head.value]) }}
                  </v-icon>
                </section>
                <section v-if="head.type == 'avatar'">
                  <v-avatar>
                    <v-img size="50" tile :src="item[head.value]"> </v-img>
                  </v-avatar>
                </section>
                <section v-if="head.type == 'duration'">
                  <v-icon small color="light-blue">mdi-timer-outline</v-icon>
                  {{ item[head.value] ? item[head.value] : "- -" | timeSymbol }}
                </section>
                <section v-if="head.type == 'status'">
                  <HealthIcon :health="item[head.value]" />
                </section>
              </Tooltip>
            </td>
            <td
              class="text-center text-capitalize"
              v-for="action in rightActions"
              :key="action.name"
            >
              <ActionsColumn
                :action="action"
                :item="item"
                :scopeName="scopeName"
              />
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
    <Loader v-if="loading" />
    <div class="text-center pa-4 pt-16 h75" v-if="!items.length && !loading">
      <h4 class="grey--text">
        <v-icon small>mdi-cancel</v-icon> Nothing to show
      </h4>
    </div>
    <div class="text-center" v-if="!loading">
      <v-pagination
        v-model="page"
        :length="pagination.total"
        circle
        :total-visible="pagination.visible"
      >
      </v-pagination>
    </div>
    <div class="counter" v-if="pagination.count">
      {{ items.length + (page - 1) * pagination.limit }} of
      {{ pagination.count }}
    </div>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import VueTypes from "vue-types";
import {
  capitalize,
  format,
  roleIcon,
  roleColor,
  initiatorIcon,
} from "../../utils";
import { Initiator } from "../../types";
import CopyToClip from "../CopyToClip.vue";
import ActionsColumn from "./ActionsColumn.vue";
import Loader from "../Loader.vue";
import HealthIcon from "./HealthIcon.vue";
import Tooltip from "@/components/Tooltip.vue";

export default Vue.extend({
  components: { CopyToClip, ActionsColumn, Loader, HealthIcon, Tooltip },
  name: "ListTable",

  data() {
    return {
      page: this.pagination.current,
    };
  },
  props: {
    items: VueTypes.any,
    headers: VueTypes.any,
    actions: VueTypes.any.def([]),
    options: VueTypes.any.def({}),
    loading: VueTypes.bool.def(false),
    pagination: VueTypes.any.def({
      current: 1,
      total: 1,
      visible: 7,
      limit: 10,
      count: 0,
    }),
    search: VueTypes.string.def(""),
    scopeName: VueTypes.string,
  },
  methods: {
    capitalize,
    getInitiatorIcon(initiator: Initiator | undefined): string {
      const type = initiator?.type || 0;
      return initiatorIcon[type];
    },
    initiatorName(initiator: Initiator | undefined): string {
      return initiator?.name || "- -";
    },
    format,
    getRoleIcon(role: number) {
      return roleIcon[role];
    },
    getRoleColor(role: number) {
      return roleColor[role];
    },
    tooltipContent(head: any, item: any): string {
      return head?.tooltip
        ? (typeof head?.tooltip == "function"
            ? head.tooltip(item)
            : item.tooltip) || item.name
        : "";
    },
  },
  watch: {
    page(val) {
      this.$emit("page-change", val);
    },
  },
  computed: {
    tableHeight() {
      return window.innerHeight;
    },
    labels(): string[] {
      const labels = this.$store.getters["Users/getUserLabels"];
      console.log(labels);

      return labels;
    },
    rightActions() {
      return this.actions.filter((action: any) => !action.left);
    },
    leftActions() {
      return this.actions.filter((action: any) => action.left);
    },
  },
});
</script>
<style scoped>
.counter {
  position: absolute;
  bottom: 0;
  right: 5px;
}
th,
td {
  padding: 0 !important;
}
</style>
